import { t } from "i18next";
import {
  AlarmClockCheck,
  AudioLines,
  BookType,
  FileText,
  Globe,
  Languages,
  Link,
  type LucideIcon,
  Mic,
  MousePointerClick,
  Rocket,
  Shield,
} from "lucide-react";
import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";
import { interfaceLanguageOptions } from "@/common/constants";
import { DefaultRecordingTemplates } from "@/common/enums/enums";
import i18n from "@/common/i18n";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/Combobox";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  CreateTemplateLink,
  SpecialtyPicker,
  TemplateSuggestions,
} from "@/components/ui/guided/WelcomeChoices";
import { OnboardingMicTest } from "@/components/ui/onboarding/onboarding-mic-test";
import {
  V3_BUTTON_SECONDARY as BUTTON_SECONDARY,
  V3_PANEL as PANEL,
  V3_BUTTON_PRIMARY,
  V3_FIELD,
  V3_MENU,
} from "@/components/ui/redesign/v3";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { underV3, V3_ICON_TILE } from "@/components/ui/v3/flagged";
import { V3Logo } from "@/components/ui/v3/logo";
import { useTemplatePreferences } from "@/hooks/templates/useTemplatePreferences";
import { TranscriptionLanguageWhisperAvailable } from "@/hooks/transcriber/types";
import { useNoteLanguage } from "@/hooks/user/useNoteLanguage";
import { inputCodeForInterface } from "@/lib/dictation-language";
import { builtInTemplateLabel } from "@/lib/document-name";
import { useFeature } from "@/lib/early-access";
import { trackOnboarding } from "@/lib/guided/events";
import {
  occupationForSpecialty,
  type Specialty,
  specialtyFromOccupation,
  suggestedTemplates,
} from "@/lib/guided/specialty";
import { inputLanguageDisplayName } from "@/lib/note-language";
import {
  dismissOnboarding,
  markOnboarded,
  markPostStepSeen,
} from "@/lib/onboarding";
import { writeLocalDefault } from "@/lib/template-preferences";
import { useTemplateList } from "@/lib/template-queries";
import { cn } from "@/lib/utils";
import { useAppSettingsStore } from "@/store/appSettings.store";
import { useAuthStore } from "@/store/auth.store";
import { useGuidedChecklistStore } from "@/store/guidedChecklist.store";
import { useOccupationsStore } from "@/store/occupations.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";

// The main action: the v3.2 ink in the v3.2 app, brand otherwise.
const buttonPrimary = (): string =>
  cn(
    V3_BUTTON_PRIMARY,
    underV3("bg-v3-ink text-v3-on-ink hover:bg-v3-ink hover:opacity-90"),
  );

interface OnboardingCardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartRecording?: () => void;
  postStep?: boolean;
  // A brand-new account (no visits yet): the conversation language starts
  // from the interface language, and the note language follows it ("Same as
  // conversation"). Existing accounts keep their saved note language.
  firstRun?: boolean;
  // Guided first visit: the summary offers a practice consulta first.
  onStartPractice?: () => void;
  // Guided first visit: "Create your own template" under the suggestions
  // (the welcome is finished first, with the choices made so far).
  onCreateTemplate?: () => void;
}

// Redesign v3.2 look (the --v3-* tokens): neutral panel and surfaces,
// hairline borders, colour only for meaning, light and dark from the same
// classes.
const DIALOG =
  "rounded-[18px] border-0 bg-v3-panel p-8 font-v3 text-v3-text antialiased shadow-v3-pop outline-hidden";
const HERO_TITLE =
  "font-v3-display text-[26px] font-semibold leading-tight tracking-[-0.015em] text-v3-text sm:text-[30px]";
const HERO_SUBTITLE = "mt-1.5 text-[15px] leading-normal text-v3-text-2";
// Focus the dialog itself (read from its title), not its first control, so
// nothing looks selected when it opens; Tab still reaches every control.
const focusDialog = (event: Event): void => {
  event.preventDefault();
  (event.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
};

// On phones the step content is taller than the dialog, so the action row
// sticks to the bottom edge of the scrolling dialog. Sticky offsets are
// measured inside the dialog's p-8, so -bottom-8/pb-8/-mb-8 make the bar
// cover that padding instead of leaving a gap. From sm up: a plain footer.
const STICKY_ACTIONS =
  "max-sm:sticky max-sm:-bottom-8 max-sm:z-10 max-sm:-mb-8 max-sm:bg-v3-panel max-sm:pb-8 max-sm:shadow-[0_-1px_0_var(--v3-border)]";

const StepBadge = ({ number }: { number: number }): JSX.Element => (
  <span
    className={cn(
      "flex size-6 shrink-0 items-center justify-center rounded-full bg-v3-brand text-[12px] font-semibold tabular-nums text-v3-on-brand",
      // v3.2: a quiet numbered step, not a filled dot.
      underV3("bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline-strong"),
    )}
  >
    {number}
  </span>
);

const StepTitle = ({
  number,
  title,
  subtitle,
}: {
  number: number;
  title: string;
  subtitle?: string;
}): JSX.Element => (
  <div className="flex items-start gap-3">
    <StepBadge number={number} />
    <div className="space-y-0.5">
      <p className="text-[15px] font-semibold leading-6 tracking-[-0.01em] text-v3-text">
        {title}
      </p>
      {subtitle && (
        <p className="text-[13.5px] leading-snug text-v3-text-2">{subtitle}</p>
      )}
    </div>
  </div>
);

const TipCard = ({
  imageSrc,
  title,
  description,
}: {
  imageSrc: string;
  title: string;
  description: string;
}): JSX.Element => (
  <div className={cn("w-full max-w-[232px] overflow-hidden", PANEL)}>
    {/* The illustrations have transparent backgrounds and sit on the
        theme's surface. */}
    <div className="flex h-[140px] items-center justify-center bg-v3-surface-2 sm:h-[181px]">
      <img
        alt={title}
        className="h-full w-auto max-w-full object-contain sm:h-[181px] sm:w-[231px]"
        src={imageSrc}
      />
    </div>
    <div className="space-y-1 px-4 py-3.5 text-center">
      <p className="text-[15px] font-semibold tracking-[-0.01em] text-v3-text">
        {title}
      </p>
      <p className="text-[13px] leading-snug text-v3-text-2">{description}</p>
    </div>
  </div>
);

const TipsStep = ({
  onBack,
  onContinue,
}: {
  onBack: () => void;
  onContinue: () => void;
}): JSX.Element => (
  <div className="flex h-full flex-col">
    <DialogTitle className="sr-only">{t("onboarding.tips.title")}</DialogTitle>
    <div className="mb-8 text-center">
      <p className={HERO_TITLE}>{t("onboarding.tips.title")}</p>
      <p className={HERO_SUBTITLE}>{t("onboarding.tips.subtitle")}</p>
    </div>

    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-stretch sm:justify-center sm:gap-6">
      <TipCard
        description={t("onboarding.tips.card1.description")}
        imageSrc={`${import.meta.env.BASE_URL}images/onboarding/tip-phone-distance.png`}
        title={t("onboarding.tips.card1.title")}
      />
      <TipCard
        description={t("onboarding.tips.card2.description")}
        imageSrc={`${import.meta.env.BASE_URL}images/onboarding/tip-lock-screen.png`}
        title={t("onboarding.tips.card2.title")}
      />
      <TipCard
        description={t("onboarding.tips.card3.description")}
        imageSrc={`${import.meta.env.BASE_URL}images/onboarding/tip-mobile-quality.png`}
        title={t("onboarding.tips.card3.title")}
      />
    </div>

    <div className={`mt-auto flex justify-center gap-3 pt-6 ${STICKY_ACTIONS}`}>
      <Button
        className={cn(BUTTON_SECONDARY, "min-w-[104px]")}
        variant="outline"
        onClick={onBack}
      >
        {t("onboarding.tips.back")}
      </Button>
      <Button
        className={cn(buttonPrimary(), "min-w-[104px]")}
        onClick={onContinue}
      >
        {t("onboarding.form.submit")}
      </Button>
    </div>
  </div>
);

const SummaryRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}): JSX.Element => (
  <div className="flex min-h-[52px] items-center justify-between gap-3 px-4 py-2.5 [&+&]:shadow-[inset_0_1px_0_var(--v3-border)]">
    <div className="flex min-w-0 items-center gap-3">
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-lg bg-v3-brand-tint text-v3-brand",
          underV3(V3_ICON_TILE),
        )}
      >
        <Icon className="size-4" strokeWidth={2} />
      </span>
      <span className="text-[14px] font-medium text-v3-text">{label}</span>
    </div>
    <span className="text-right text-[14px] text-v3-text-2">{value}</span>
  </div>
);

const SummaryStep = ({
  template,
  noteLanguageLabel,
  displayLanguageLabel,
  isRealtimeEnabled,
  onMaybeLater,
  onStartRecording,
  onStartPractice,
}: {
  template: string;
  noteLanguageLabel: string;
  displayLanguageLabel: string;
  isRealtimeEnabled: boolean;
  onMaybeLater: () => void;
  onStartRecording: () => void;
  onStartPractice?: () => void;
}): JSX.Element => (
  <div className="flex h-full flex-col">
    <DialogTitle className="sr-only">
      {t("onboarding.summary.title")}
    </DialogTitle>
    <div className="flex flex-col items-center gap-3">
      <img
        alt=""
        className="h-[80px] w-[74px]"
        src={`${import.meta.env.BASE_URL}images/onboarding/onboarding-summary.png`}
      />
      <div className="text-center">
        <p className={HERO_TITLE}>{t("onboarding.summary.title")}</p>
        <p className={HERO_SUBTITLE}>
          {onStartPractice
            ? t("guided.welcome.summarySubtitle")
            : t("onboarding.summary.subtitle")}
        </p>
      </div>
    </div>

    <div className={cn("mx-auto mt-6 w-full max-w-[560px]", PANEL)}>
      <SummaryRow
        icon={Mic}
        label={t("onboarding.summary.microphone")}
        value={t("onboarding.summary.micValue")}
      />
      <SummaryRow
        icon={Languages}
        label={t("onboarding.summary.displayLanguage")}
        value={displayLanguageLabel}
      />
      <SummaryRow
        icon={BookType}
        label={t("onboarding.summary.noteLanguage")}
        value={noteLanguageLabel}
      />
      <SummaryRow
        icon={FileText}
        label={t("onboarding.summary.template")}
        value={template}
      />
      <SummaryRow
        icon={AudioLines}
        label={t("onboarding.summary.mode")}
        value={
          isRealtimeEnabled
            ? t("onboarding.summary.modeRealtime")
            : t("onboarding.summary.modeNonRealtime")
        }
      />
    </div>

    <div
      className={`mx-auto mt-auto flex w-full max-w-[560px] flex-col-reverse gap-2 pt-6 sm:flex-row sm:justify-end ${STICKY_ACTIONS}`}
    >
      <Button
        className={cn(BUTTON_SECONDARY, "w-full sm:w-auto")}
        variant="outline"
        onClick={onMaybeLater}
      >
        {t("onboarding.summary.maybeLater")}
      </Button>
      {onStartPractice ? (
        <>
          <Button
            className={cn(BUTTON_SECONDARY, "w-full sm:w-auto")}
            data-testid="onboarding-start-recording"
            variant="outline"
            onClick={onStartRecording}
          >
            {t("onboarding.summary.startRecording")}
          </Button>
          <Button
            className={cn(buttonPrimary(), "w-full sm:w-auto")}
            data-testid="onboarding-start-practice"
            onClick={onStartPractice}
          >
            {t("guided.practice.cta")}
          </Button>
        </>
      ) : (
        <Button
          className={cn(buttonPrimary(), "w-full sm:w-auto")}
          onClick={onStartRecording}
        >
          {t("onboarding.summary.startRecording")}
        </Button>
      )}
    </div>
  </div>
);

const PostRecordingStep = ({
  onMaybeLater,
  onInstallExtension,
}: {
  onMaybeLater: () => void;
  onInstallExtension: () => void;
}): JSX.Element => {
  const Card = ({
    icon: Icon,
    title,
  }: {
    icon: LucideIcon;
    title: string;
  }): JSX.Element => (
    <div className="flex items-center justify-center rounded-v3-panel bg-v3-surface-2 p-4 shadow-v3-hairline sm:p-5">
      <div className="flex flex-col items-center gap-3 text-center">
        <span
          className={cn(
            "flex size-11 items-center justify-center rounded-full bg-v3-brand-tint text-v3-brand",
            underV3(V3_ICON_TILE),
          )}
        >
          <Icon className="size-5" strokeWidth={2} />
        </span>
        <p className="text-[14px] font-semibold leading-snug text-v3-text sm:text-[15px]">
          {title}
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <DialogTitle className="sr-only">
        {t("onboarding.postRecording.title")}
      </DialogTitle>
      <div className="mb-6 text-center">
        <p className={HERO_TITLE}>{t("onboarding.postRecording.title")}</p>
        <p className={HERO_SUBTITLE}>
          {t("onboarding.postRecording.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <Card
          icon={MousePointerClick}
          title={t("settings.chromeExtension.features.oneClick")}
        />
        <Card
          icon={Link}
          title={t("settings.chromeExtension.features.emrSupport")}
        />
        <Card
          icon={AlarmClockCheck}
          title={t("settings.chromeExtension.features.saveTime")}
        />
        <Card icon={Rocket} title={t("onboarding.postRecording.setup")} />
      </div>

      <div
        className={`mt-auto flex flex-col-reverse gap-2 pt-6 sm:flex-row sm:justify-end ${STICKY_ACTIONS}`}
      >
        <Button
          className={cn(BUTTON_SECONDARY, "w-full sm:w-auto")}
          variant="outline"
          onClick={onMaybeLater}
        >
          {t("onboarding.summary.maybeLater")}
        </Button>
        <Button
          className={cn(buttonPrimary(), "w-full sm:w-auto")}
          onClick={onInstallExtension}
        >
          <Mic className="size-4" strokeWidth={2.2} />
          {t("onboarding.postRecording.install")}
        </Button>
      </div>
    </div>
  );
};

const TranscriptionModeOptions = (): JSX.Element => {
  const { isRealtimeEnabled, saveIsRealtimeEnabled } =
    useRecordingSettingsStore();

  const OptionRow = ({
    selected,
    title,
    hint,
    recommended = false,
    onSelect,
  }: {
    selected: boolean;
    title: string;
    hint: string;
    recommended?: boolean;
    onSelect: () => void;
  }): JSX.Element => (
    <button
      aria-pressed={selected}
      className={cn(
        "flex min-h-[56px] w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left transition-colors",
        selected
          ? "bg-v3-panel shadow-[0_0_0_1px_var(--v3-border-strong),0_1px_2px_rgba(0,0,0,0.06)]"
          : "hover:bg-v3-surface-3",
      )}
      type="button"
      onClick={onSelect}
    >
      <span
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-full",
          selected
            ? cn("bg-v3-brand", underV3("bg-v3-ink"))
            : "shadow-[inset_0_0_0_1.5px_var(--v3-text-3)]",
        )}
      >
        {selected && <span className="size-1.5 rounded-full bg-v3-panel" />}
      </span>
      <span className="flex-1">
        <span className="flex flex-wrap items-center gap-2 text-[14px] font-semibold text-v3-text">
          {title}
          {recommended && (
            <span className="inline-flex h-5 items-center rounded-full bg-v3-surface-3 px-2 text-[11.5px] font-semibold text-v3-text-2">
              {t("onboarding.form.recommended")}
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-[12.5px] leading-snug text-v3-text-2">
          {hint}
        </span>
      </span>
    </button>
  );

  // A segmented choice: two options on the theme's sunken surface.
  return (
    <div className="flex flex-col gap-1 rounded-xl bg-v3-surface-2 p-1 shadow-v3-hairline">
      <OptionRow
        recommended
        hint={t("onboarding.form.transcriptionRealtimeHint")}
        selected={isRealtimeEnabled}
        title={t("onboarding.form.transcriptionRealtime")}
        onSelect={() => {
          saveIsRealtimeEnabled(true);
        }}
      />
      <OptionRow
        hint={t("onboarding.form.transcriptionNonRealtimeHint")}
        selected={!isRealtimeEnabled}
        title={t("onboarding.form.transcriptionNonRealtime")}
        onSelect={() => {
          saveIsRealtimeEnabled(false);
        }}
      />
    </div>
  );
};

// The conversation language shown on the set-up step. A first run starts
// from the interface language (a Portuguese interface records Portuguese)
// unless this browser already has a non-default choice; otherwise the
// recorder's saved choice, as before.
export const onboardingConversationLanguage = ({
  chosen,
  saved,
  interfaceLanguage,
  firstRun,
}: {
  chosen: string | null;
  saved: string | null | undefined;
  interfaceLanguage: string | undefined;
  firstRun: boolean;
}): string => {
  if (chosen) return chosen;
  const savedValue = saved && saved !== "auto" ? saved : null;
  if (firstRun && (!savedValue || savedValue === "eng"))
    return inputCodeForInterface(interfaceLanguage) ?? savedValue ?? "eng";
  return savedValue ?? "eng";
};

// The note language that goes with the conversation language on the
// welcome, as an input code: the same language, or the interface language
// when the conversation is "Detect automatically".
export const onboardingNoteInput = (
  conversation: string,
  interfaceLanguage: string | undefined,
): string | undefined =>
  conversation === "auto"
    ? inputCodeForInterface(interfaceLanguage)
    : conversation;

export const OnboardingCard = (props: OnboardingCardProps): JSX.Element =>
  props.open ? <OnboardingDialog {...props} /> : <></>;

const OnboardingDialog = ({
  open,
  onOpenChange,
  onStartRecording,
  postStep = false,
  firstRun = false,
  onStartPractice,
  onCreateTemplate,
}: OnboardingCardProps): JSX.Element => {
  // Guided first visit (VITE_GUIDED_FIRST_VISIT_ENABLED): specialty and
  // suggested templates; on a first run one language for the app, the
  // consulta and the note; the summary offers a practice consulta.
  const guided = useFeature("guided-first-visit");
  const unifiedLanguage = guided && firstRun;
  const [otherConversation, setOtherConversation] = useState(false);
  const [step, setStep] = useState<"greeting" | "form" | "tips" | "summary">(
    "greeting",
  );
  const [chosenTemplate, setTemplate] = useState<string | null>(null);
  const [chosenInput, setChosenInput] = useState<string | null>(null);
  const noteLanguage = useNoteLanguage();
  const [container, setContainer] = useState<HTMLElement | null>(null);
  // Fixed desktop heights; on phones the dialog fills the content area and
  // scrolls (see STICKY_ACTIONS).
  const cardHeight = postStep
    ? "h-full sm:h-[602px]"
    : step === "form"
      ? "h-full sm:h-[702px]"
      : step === "tips" || step === "summary"
        ? "h-full sm:h-[602px]"
        : "h-[540px]";

  const { userData } = useAuthStore.getState();
  const { inputLanguage, saveInputLanguage } =
    useRecordingSettingsStore.getState();
  const { setInputLanguage } = useAppSettingsStore.getState();
  const { templates } = useTemplateList();
  const { setDefault, defaultTemplate } = useTemplatePreferences();
  // The sign-up occupation gives the specialty; the first template suggested
  // for it is the first run's default.
  const occupation = userData?.userPreference?.occupation;
  const [specialty, setSpecialty] = useState<Specialty | null>(() =>
    specialtyFromOccupation(occupation),
  );
  const template =
    chosenTemplate ??
    (guided && firstRun ? suggestedTemplates(specialty)[0] : defaultTemplate) ??
    defaultTemplate;
  const occupations = useOccupationsStore((state) => state.occupations);
  useEffect(() => {
    if (guided && occupations.length === 0)
      void useOccupationsStore.getState().fetchAndSetOccupations();
  }, [guided, occupations.length]);

  useEffect(() => {
    setContainer(document.getElementById("app-content"));
  }, [userData?.public_id]);

  const handleTemplateChange = (value: string): void => {
    setTemplate(value);
  };

  // A conversation language picked here is the doctor's default for the
  // recorder and the note at once, saved as soon as it is picked: closing the
  // welcome without finishing must not leave the recorder in Portuguese and
  // the note in English (Dilyar, 31/10).
  const pickConversation = (code: string): void => {
    setChosenInput(code);
    saveInputLanguage(code);
    setInputLanguage(code);
    void noteLanguage.chooseSameAsConversation(
      onboardingNoteInput(code, i18n.language),
    );
  };

  const handleFinish = (): void => {
    if (firstRun || chosenInput) {
      // Save the conversation language shown (on a first run it may be the
      // interface default, never picked), and write the note in it.
      saveInputLanguage(inputLanguageValue);
      setInputLanguage(inputLanguageValue);
      void noteLanguage.chooseSameAsConversation(
        onboardingNoteInput(inputLanguageValue, i18n.language),
      );
    }
    // Failed account saves stay local for the one-time move on the next load.
    void setDefault(template).then((saved) => {
      if (!saved) writeLocalDefault(userData?.public_id, template);
    });
    if (guided) {
      // A specialty other than the sign-up occupation is saved as the
      // account's occupation (the same field as Settings > Profile).
      const chosen = specialty ? occupationForSpecialty(specialty) : null;
      const match = chosen
        ? occupations.find(
            (entry) => entry.occupation.trim().toLowerCase() === chosen,
          )
        : undefined;
      if (
        match &&
        specialty !== specialtyFromOccupation(occupation) &&
        typeof match.id === "number"
      )
        // Loaded on demand: the welcome itself does not need the API client.
        void import("@/lib/guided/writes").then(({ saveOccupation }) =>
          saveOccupation(match.id),
        );
      useGuidedChecklistStore.getState().complete("setup");
      trackOnboarding("welcome_completed", {
        language: i18n.language,
        template,
        specialty: specialty ?? "none",
      });
    }
    markOnboarded(userData?.public_id);
    onOpenChange(false);
  };

  // One language for the app, the consulta and the note (first run).
  const changeInterfaceLanguage = (lang: string): void => {
    void i18n.changeLanguage(lang);
    if (!unifiedLanguage) return;
    const code = inputCodeForInterface(lang);
    if (!code) return;
    pickConversation(code);
  };

  const inputLanguageValue = onboardingConversationLanguage({
    chosen: chosenInput,
    saved: inputLanguage,
    interfaceLanguage: i18n.language,
    firstRun,
  });
  // Language names follow the interface language, as in Advanced settings.
  const inputLanguageOptions = TranscriptionLanguageWhisperAvailable.map(
    (language) => ({
      value: language.value,
      label:
        language.value === "auto"
          ? t("noteLanguage.detectAutomatically")
          : inputLanguageDisplayName(
              language.value,
              i18n.language,
              t("noteLanguage.mandarin"),
            ),
    }),
  );

  const templateOptions = [
    ...Object.values(DefaultRecordingTemplates).map((name) => ({
      value: name,
      label: builtInTemplateLabel(name),
    })),
    ...templates.map((tpl) => ({
      value: String(tpl.public_id),
      label: tpl.name,
    })),
  ];

  const resetForm = useCallback(() => {
    setStep("greeting");
    setTemplate(null);
    setChosenInput(null);
  }, []);

  if (postStep) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next) {
            markPostStepSeen();
            resetForm();
          }
        }}
      >
        <DialogContent
          className={`fixed grid-cols-1 w-[760px] max-w-full max-h-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-y-auto ${DIALOG} ${cardHeight}`}
          container={container}
          overlayClassName="bg-black/25"
          onOpenAutoFocus={focusDialog}
        >
          <PostRecordingStep
            onInstallExtension={() => {
              markPostStepSeen();
              window.open(
                "https://chromewebstore.google.com/detail/bbgcekhigafammpphfipefklildgjmfh",
                "_blank",
                "noopener",
              );
              onOpenChange(false);
            }}
            onMaybeLater={() => {
              markPostStepSeen();
              onOpenChange(false);
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closed without finishing: do not show it again on the next load.
        if (!next) dismissOnboarding(userData?.public_id);
        onOpenChange(next);
        if (!next) resetForm();
      }}
    >
      <DialogContent
        className={`absolute grid-cols-1 w-[919px] max-w-full max-h-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-y-auto ${DIALOG} ${cardHeight}`}
        container={container}
        overlayClassName="bg-black/25"
        onOpenAutoFocus={focusDialog}
      >
        {step === "greeting" ? (
          <>
            <div className="absolute left-6 top-6">
              <Select
                value={i18n.language}
                onValueChange={changeInterfaceLanguage}
              >
                <SelectTrigger
                  aria-label={
                    unifiedLanguage
                      ? t("guided.welcome.language")
                      : t("onboarding.form.displayLanguage")
                  }
                  className="h-11 w-auto gap-2 rounded-full border-0 bg-v3-surface-2 pl-3 pr-3 font-v3 text-[13.5px] font-medium text-v3-text shadow-v3-hairline hover:bg-v3-surface-3 focus:ring-2 focus:ring-v3-brand-ring sm:h-9"
                  data-testid="onboarding-language"
                >
                  <Globe aria-hidden className="size-4 text-v3-text-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={V3_MENU}>
                  {interfaceLanguageOptions.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex h-full flex-col items-center justify-center gap-5 py-12 text-center">
              <V3Logo size={56} wordmark={false} />
              <DialogTitle className="font-v3-display text-[30px] font-semibold leading-tight tracking-[-0.02em] text-v3-text sm:text-[36px]">
                {t("onboarding.greeting.title", {
                  name: `${userData?.firstname ?? ""} ${
                    userData?.lastname ?? ""
                  }`.trim(),
                })}
              </DialogTitle>
              <p className="max-w-[520px] text-[16px] leading-relaxed text-v3-text-2 sm:text-[17px]">
                {t("onboarding.greeting.description")}
              </p>
              <Button
                className={cn(buttonPrimary(), "mt-1")}
                onClick={() => {
                  setStep("form");
                }}
              >
                {t("onboarding.getStarted")}
              </Button>
            </div>
          </>
        ) : step === "form" ? (
          <div className="flex h-full flex-col">
            <DialogTitle className="sr-only">
              {t("onboarding.form.title")}
            </DialogTitle>
            <div className="mb-6 text-center">
              <p className={HERO_TITLE}>{t("onboarding.form.title")}</p>
              <p className={HERO_SUBTITLE}>{t("onboarding.form.subtitle")}</p>
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:gap-5">
              <div className={cn("flex-1 p-5", PANEL)}>
                <div className="flex flex-col gap-4">
                  <StepTitle
                    number={1}
                    subtitle={t("onboarding.form.checkMicrophoneSubtitle")}
                    title={t("onboarding.form.checkMicrophone")}
                  />
                  <OnboardingMicTest />

                  <StepTitle
                    number={2}
                    title={t("onboarding.form.transcriptionMode")}
                  />
                  <TranscriptionModeOptions />

                  <div className="flex items-center gap-2 text-v3-text-3">
                    <Shield className="size-3.5" />
                    <span className="text-[12px] font-medium">
                      {t("onboarding.form.privacyNote")}
                    </span>
                  </div>
                </div>
              </div>

              {guided ? (
                <div className={cn("flex-1 p-5", PANEL)}>
                  <div className="flex flex-col gap-4">
                    <StepTitle
                      number={3}
                      subtitle={
                        unifiedLanguage
                          ? t("guided.welcome.languageHint", {
                              language:
                                inputLanguageOptions.find(
                                  (lang) => lang.value === inputLanguageValue,
                                )?.label ?? "",
                            })
                          : undefined
                      }
                      title={
                        unifiedLanguage
                          ? t("guided.welcome.language")
                          : t("onboarding.form.displayLanguage")
                      }
                    />
                    <Select
                      value={i18n.language}
                      onValueChange={changeInterfaceLanguage}
                    >
                      <SelectTrigger
                        className={V3_FIELD}
                        data-testid="guided-language"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={V3_MENU}>
                        {interfaceLanguageOptions.map((lang) => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {unifiedLanguage && !otherConversation ? (
                      <button
                        className="-mt-2 self-start text-[13px] font-medium text-v3-text-2 underline decoration-v3-border-strong underline-offset-4 hover:text-v3-text"
                        data-testid="guided-other-conversation"
                        type="button"
                        onClick={() => setOtherConversation(true)}
                      >
                        {t("guided.welcome.otherConversation")}
                      </button>
                    ) : (
                      <>
                        {!unifiedLanguage && (
                          <StepTitle
                            number={4}
                            title={t("onboarding.form.conversationLanguage")}
                          />
                        )}
                        <Combobox
                          chevron="down"
                          items={inputLanguageOptions}
                          contentClassName={V3_MENU}
                          triggerClassName={V3_FIELD}
                          value={inputLanguageValue}
                          onValueChange={({ value }) => pickConversation(value)}
                        />
                      </>
                    )}

                    <StepTitle
                      number={unifiedLanguage ? 4 : 5}
                      subtitle={t("guided.welcome.specialtyHint")}
                      title={t("guided.welcome.specialty")}
                    />
                    <SpecialtyPicker
                      value={specialty}
                      onChange={(next) => {
                        setSpecialty(next);
                        // A new specialty brings its own suggestion unless a
                        // template was picked.
                        if (
                          chosenTemplate &&
                          !suggestedTemplates(next).includes(
                            chosenTemplate as never,
                          )
                        )
                          setTemplate(null);
                      }}
                    />

                    <StepTitle
                      number={unifiedLanguage ? 5 : 6}
                      title={t("guided.welcome.templates")}
                    />
                    <TemplateSuggestions
                      specialty={specialty}
                      value={template}
                      onChange={handleTemplateChange}
                    />
                    {onCreateTemplate && (
                      <CreateTemplateLink
                        onClick={() => {
                          trackOnboarding("create_template_opened", {
                            from: "welcome",
                          });
                          handleFinish();
                          onCreateTemplate();
                        }}
                      />
                    )}
                    {templates.length > 0 && (
                      <Select
                        value={template}
                        onValueChange={handleTemplateChange}
                      >
                        <SelectTrigger
                          aria-label={t("guided.welcome.otherTemplate")}
                          className={V3_FIELD}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={V3_MENU}>
                          {templateOptions.map((tpl) => (
                            <SelectItem key={tpl.value} value={tpl.value}>
                              {tpl.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              ) : (
                <div className={cn("flex-1 p-5", PANEL)}>
                  <div className="flex flex-col gap-4">
                    <StepTitle
                      number={3}
                      title={t("onboarding.form.displayLanguage")}
                    />
                    <Select
                      value={i18n.language}
                      onValueChange={(lang) => {
                        void i18n.changeLanguage(lang);
                      }}
                    >
                      <SelectTrigger className={V3_FIELD}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={V3_MENU}>
                        {interfaceLanguageOptions.map((lang) => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* This control sets the language of the conversation (what
                      is transcribed). On a first run the note follows it. */}
                    <StepTitle
                      number={4}
                      subtitle={
                        firstRun
                          ? t("onboarding.form.conversationLanguageHint")
                          : undefined
                      }
                      title={t("onboarding.form.conversationLanguage")}
                    />
                    <Combobox
                      chevron="down"
                      items={inputLanguageOptions}
                      contentClassName={V3_MENU}
                      triggerClassName={V3_FIELD}
                      value={inputLanguageValue}
                      onValueChange={({ value }) => pickConversation(value)}
                    />

                    <StepTitle
                      number={5}
                      title={t("onboarding.form.defaultTemplate")}
                    />
                    <Select
                      value={template}
                      onValueChange={handleTemplateChange}
                    >
                      <SelectTrigger className={V3_FIELD}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={V3_MENU}>
                        {templateOptions.map((tpl) => (
                          <SelectItem key={tpl.value} value={tpl.value}>
                            {tpl.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <div className={`mt-auto flex justify-end pt-6 ${STICKY_ACTIONS}`}>
              <Button
                className={cn(buttonPrimary(), "min-w-[104px]")}
                onClick={() => {
                  setStep("tips");
                }}
              >
                {t("onboarding.form.submit")}
              </Button>
            </div>
          </div>
        ) : step === "tips" ? (
          <TipsStep
            onBack={() => {
              setStep("form");
            }}
            onContinue={() => {
              setStep("summary");
            }}
          />
        ) : (
          <SummaryStep
            template={
              templateOptions.find((tpl) => tpl.value === template)?.label ??
              template
            }
            displayLanguageLabel={
              interfaceLanguageOptions.find(
                (lang) => lang.value === i18n.language,
              )?.label ?? ""
            }
            isRealtimeEnabled={
              useRecordingSettingsStore.getState().isRealtimeEnabled
            }
            noteLanguageLabel={
              // First run, or a conversation language picked here: the note
              // is written in it. Otherwise the account's note language.
              firstRun || chosenInput
                ? (inputLanguageOptions.find(
                    (lang) =>
                      lang.value ===
                      onboardingNoteInput(inputLanguageValue, i18n.language),
                  )?.label ??
                  noteLanguage.current?.label ??
                  "")
                : (noteLanguage.current?.label ?? "")
            }
            onMaybeLater={handleFinish}
            onStartRecording={() => {
              handleFinish();
              onStartRecording?.();
            }}
            onStartPractice={
              guided && onStartPractice
                ? () => {
                    handleFinish();
                    onStartPractice();
                  }
                : undefined
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
